import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  Spinner,
  Flex,
  ModalCloseButton,
} from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNoticeTypeContext } from "@root/context/notice-type-context";
import { RECIPIENT_TYPES } from "@root/constants";
import CustomSelect from "../common/CustomSelect";

interface AddNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  initialValues?: any;
  addNoticeLoading: boolean;
}

const DEFAULT_POST_TO = [
  RECIPIENT_TYPES.SUBSCRIBER,
  RECIPIENT_TYPES.JOURNALIST,
];

const AddNoticeModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  addNoticeLoading,
}: AddNoticeModalProps) => {
  const { t } = useTranslation("common");
  const { noticeTypeData, fetchNoticeType } = useNoticeTypeContext();

  const [title, setTitle] = useState(initialValues?.title || "");
  const [type, setType] = useState(
    initialValues?.noticeTypeId ? String(initialValues.noticeTypeId) : "",
  );
  const [content, setContent] = useState(initialValues?.description || "");
  const [visibility, setVisibility] = useState(
    initialValues?.isPublish !== undefined ? initialValues.isPublish : true,
  );
  const [publishedAt, setPublishedAt] = useState(
    initialValues?.publishedAt || "",
  );
  const [postTo, setPostTo] = useState<number[]>(DEFAULT_POST_TO);
  const [files, setFiles] = useState<File[]>(initialValues?.documents || []);
  const [typeError, setTypeError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetchNoticeType({});
  }, []);

  const resetForm = () => {
    setTitle("");
    setType("");
    setContent("");
    setVisibility(true);
    setPublishedAt("");
    setPostTo(DEFAULT_POST_TO);
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (isOpen) {
      if (initialValues?.documents) {
        setFiles(initialValues?.documents);
      } else {
        setFiles([]);
      }

      if (fileInputRef.current) fileInputRef.current.value = "";

      if (initialValues) {
        setTitle(initialValues?.title || "");
        setType(
          initialValues?.noticeTypeId ? String(initialValues.noticeTypeId) : "",
        );
        setContent(initialValues?.description || "");
        setVisibility(
          initialValues?.isPublish !== undefined
            ? initialValues.isPublish
            : true,
        );
        setPublishedAt(initialValues?.publishedAt || "");
        setPostTo(
          initialValues?.recipientType
            ? [
                RECIPIENT_TYPES.SUBSCRIBER,
                RECIPIENT_TYPES.JOURNALIST,
                RECIPIENT_TYPES.NON_AUTH,
              ].filter((bit) => (initialValues.recipientType & bit) !== 0)
            : DEFAULT_POST_TO,
        );
      } else {
        resetForm();
      }
    }
  }, [isOpen, initialValues]);

  useEffect(() => {
    if (!isOpen) {
      console.log("Modal closed — cleaning up files");
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!type) {
      setTypeError(true);
      return;
    }
    setTypeError(false);
    const payload = {
      title,
      recipientType: postTo.reduce((acc, bit) => acc | bit, 0),
      description: content,
      noticeTypeId: Number(type),
      isPublish: visibility,
      publishedAt: publishedAt || new Date().toISOString(),
      Documents: files,
    };
    console.log("Submitting notice with payload:", payload);
    onSubmit(payload);
  };

  console.log("postT`o", postTo);

  const handleClose = () => {
    console.log("Modal is closing. Executing cleanup.");
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const noticeTypeOptions =
    noticeTypeData?.map((item: any) => ({
      label: item.name,
      value: String(item.id),
    })) || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        width="600px"
        minHeight="420px"
        minWidth="812px"
        margin="auto"
        background="white"
        borderRadius="20px"
        display="flex"
        flexDirection="column"
        position="relative"
      >
        <ModalCloseButton
          top="12px"
          right="12px"
          color="black"
          _hover={{ bg: "gray.100" }}
        />
        <ModalHeader>
          {initialValues?.title
            ? t("addNoticeModal.EditNotice")
            : t("addNoticeModal.postNewNotice")}
          <Text fontSize="sm" fontWeight="400" color="#5C6670">
            {initialValues?.title
              ? t("addNoticeModal.EditNotice")
              : t("addNoticeModal.postNewNotice")}
          </Text>
        </ModalHeader>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1 }}
        >
          <ModalBody overflowY="auto" flex="1">
            <FormControl isRequired>
              <FormLabel>{t("addNoticeModal.title")}</FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("addNoticeModal.titlePh")}
                fontSize="sm"
              />
            </FormControl>

            <FormControl isRequired isInvalid={typeError} mt={2}>
              <FormLabel>{t("addNoticeModal.type")}</FormLabel>
              <CustomSelect
                value={type}
                onChange={(value) => {
                  setType(value);
                  if (value) setTypeError(false);
                }}
                placeholder={t("addNoticeModal.typePh")}
                options={noticeTypeOptions}
                width="100%"
              />
              <FormErrorMessage>
                {t("addNoticeModal.typeRequired")}
              </FormErrorMessage>
            </FormControl>

            <FormControl isRequired mt={2}>
              <FormLabel>{t("addNoticeModal.content")}</FormLabel>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t("addNoticeModal.contentPh")}
                fontSize="sm"
                resize="none"
                overflowY="auto"
              />
            </FormControl>

            <FormControl mt={3}>
              <FormLabel>{t("translate.uploadAttachment")}</FormLabel>

              <Flex
                justifyContent="center"
                alignItems="center"
                border="1px dashed #A0AEC0"
                borderRadius="10px"
                height="60px"
                position="relative"
                bg="#F9FAFB"
                _hover={{ bg: "#F1F2F6" }}
                cursor="pointer"
              >
                {/* Hidden actual file input */}
                <Input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  opacity={0}
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  cursor="pointer"
                  onChange={(e) =>
                    setFiles(e.target.files ? Array.from(e.target.files) : [])
                  }
                />

                {/* Centered text display */}
                <Text fontSize="14px" color="#4A5568" pointerEvents="none">
                  {files.length > 0
                    ? `${files.length} file(s) selected`
                    : t("addNoticeModal.chooseFiles")}
                </Text>
              </Flex>

              <Text mt={2} fontSize="12px" color="#8B919E">
                {t("translate.supportedFormats")}
              </Text>
            </FormControl>

            <FormControl mt={2}>
              <FormLabel>{t("addNoticeModal.visibility")}</FormLabel>
              <RadioGroup
                value={visibility ? "true" : "false"}
                onChange={(value) => setVisibility(value === "true")}
              >
                <Radio value="true" pr={20}>
                  {t("addNoticeModal.visible")}
                </Radio>
                <Radio value="false">{t("addNoticeModal.unvisible")}</Radio>
              </RadioGroup>
            </FormControl>

            <FormControl mt={2}>
              <FormLabel>{t("addNoticeModal.postTo")}</FormLabel>
              <CheckboxGroup
                value={postTo}
                onChange={(values) => setPostTo(values.map(Number))}
              >
                <Stack direction="row" mt={2} spacing={6}>
                  <Checkbox value={RECIPIENT_TYPES.SUBSCRIBER}>
                    {t("addNoticeModal.postToSubscriber")}
                  </Checkbox>
                  <Checkbox value={RECIPIENT_TYPES.JOURNALIST}>
                    {t("addNoticeModal.postToJournalist")}
                  </Checkbox>
                  <Checkbox value={RECIPIENT_TYPES.NON_AUTH}>
                    {t("addNoticeModal.postToNonAuth")}
                  </Checkbox>
                </Stack>
              </CheckboxGroup>
            </FormControl>
          </ModalBody>

          <ModalFooter display="flex" gap={4}>
            <Button
              type="button"
              onClick={handleClose}
              fontWeight="600"
              color="#1819B4"
              width="fit-content"
              height="fit-content"
              py={2}
              px={5}
              background="white"
              border="1px solid #1819B4"
              borderRadius="8px"
            >
              {t("addNoticeModal.cancle")}
            </Button>
            <Button
              type="submit"
              fontWeight="600"
              width="fit-content"
              height="fit-content"
              py={2}
              px={5}
              borderRadius="8px"
              background="#1819B4"
              isLoading={addNoticeLoading}
              loadingText={t("addNoticeModal.Submit")}
            >
              {addNoticeLoading ? (
                <Spinner size="sm" />
              ) : initialValues?.title ? (
                t("profile.save")
              ) : (
                t("addNoticeModal.Submit")
              )}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AddNoticeModal;
